import { getAccessToken, setAccessToken, removeAccessToken } from '../common/token'
import {
  getUsername,
  setUsername,
  removeUsername,
  getAdminId,
  setAdminId,
  removeAdminId,
  getMobile,
  removeMobile,
  setMobile
} from '../common/username'
import { LOGIN_CAPTCHA_IMAGE_URL, SLIDER_CAPTCHA_URL } from '@/api/urls'
import storeGlo from '@/store'

import AuthApi from '@/api/merchant-auth'
import MerchantAdminApi from '@/api/merchant-admin'

const state = {
  // 验证码图片URL
  captchaImageURL: process.env.BASE_API + LOGIN_CAPTCHA_IMAGE_URL,
  accessToken: getAccessToken(),
  username: getUsername(),
  adminId: getAdminId(),
  mobile: getMobile(),
  baseInfo: null
}

const mutations = {
  SET_ACCESS_TOKEN: (state, accessToken) => {
    state.accessToken = accessToken
  },
  SET_USERNAME: (state, username) => {
    state.username = username
  },
  SET_ADMIN_ID: (state, adminId) => {
    state.adminId = adminId
  },
  SET_MOBILE: (state, mobile) => {
    state.mobile = mobile
  },
  SET_BASE_INFO: (state, baseInfo) => {
    state.baseInfo = baseInfo
  }
}

const actions = {
  // 获取验证码
  async getCaptchaImage ({ commit }) {
    const resp = await AuthApi.getCaptchaImage(process.env.BASE_API + LOGIN_CAPTCHA_IMAGE_URL)
    return resp
  },
  // 获取滑块验证码
  async getSliderCaptcha ({ commit }) {
    const resp = await AuthApi.getSliderCaptcha()
    return resp
  },
  // 校验滑块验证码
  async checkSliderCaptcha ({ commit }, data) {
    const resp = await AuthApi.checkSliderCaptcha(data)
    return resp
  },
  // 登录
  async login ({ commit }, params) {
    // 登录前先清空旧的商户信息，避免显示旧数据
    await storeGlo.commit('MerchantMe/SET_MERCHANT_INFO', null)
    
    const result = await AuthApi.login(params)
    // 检查是否需要重置密码
    if (result.tokenType === 'RESET_PWD_TEMP') {
      // 返回重置密码所需的数据，不设置token
      // 注意：根据API文档，token字段可能是 result.t 或 result.token
      return {
        needResetPassword: true,
        tokenType: result.tokenType,
        token: result.t || result.token, // 兼容 t 和 token 字段
        message: result.message
      }
    }
    
    // 检查是否需要选择商户 (LOGIN_TEMP)
    if (result.tokenType === 'LOGIN_TEMP') {
      // 返回商户选择所需的数据
      // 注意：根据API文档，临时token字段可能是 result.t 而不是 result.token
      return {
        needSelectMerchant: true,
        tokenType: result.tokenType,
        token: result.t || result.token, // 优先使用 t 字段（根据API文档）
        merchants: result.merchants || [], // 商户列表
        message: result.message
      }
    }
    
    // 正常登录流程
    // 根据API文档，token字段可能是 result.t 或 result.token
    const accessToken = result.t || result.token
    if (accessToken) {
      // 验证 token 格式（JWT token 应该有 3 个部分：header.payload.signature）
      const tokenParts = accessToken.split('.')
      if (tokenParts.length !== 3) {
        // 不设置格式错误的 token，避免后续请求失败
        return {
          error: true,
          message: '登录返回的 token 格式不正确，不是有效的 JWT token'
        }
      }
      commit('SET_ACCESS_TOKEN', accessToken)
      setAccessToken(accessToken)
    } else {
    }
    if (result.username) {
      commit('SET_USERNAME', result.username)
      setUsername(result.username)
    }
    if (result.id) {
      commit('SET_ADMIN_ID', result.id)
      setAdminId(result.id)
    }
    return result
  },
  // 手机登录
  async mobileLogin ({ commit }, params) {
    // 登录前先清空旧的商户信息，避免显示旧数据
    await storeGlo.commit('MerchantMe/SET_MERCHANT_INFO', null)
    const result = await AuthApi.mobileLogin(params)
    if (result.token) {
      commit('SET_ACCESS_TOKEN', result.token)
      setAccessToken(result.token)
    }
    if (result.username) {
      commit('SET_USERNAME', result.username)
      setUsername(result.username)
    }
    if (result.id) {
      commit('SET_ADMIN_ID', result.id)
      setAdminId(result.id)
    }
  },
  async getMobileRegisterSMS ({ commit }, params) {
    const result = await AuthApi.getMobileRegisterSMS(params)
    return result
  },
  // 获取手机登录验证码
  async getMobileLoginSMS ({ commit }, params) {
    const result = await AuthApi.getMobileLoginSMS(params)
    return result
  },
  // 登出
  async logout ({ commit }) {
    removeAccessToken()
    commit('SET_ACCESS_TOKEN', null)
    removeUsername()
    commit('SET_USERNAME', null)
    await removeAdminId()
    commit('SET_ADMIN_ID', null)
    commit('SET_ADMIN_ID', null)
    commit('SET_MOBILE', null)
    await removeMobile()
    await storeGlo.commit('MerchantMe/SET_MERCHANT_INFO', {})
  },
  async getMerchantBaseInfos ({ commit }) {
    const result = await AuthApi.getMerchantBaseInfos()
    return result
  },
  async queryBaseInfos ({ commit }, params) {
    const result = await AuthApi.queryBaseInfos(params)
    return result
  },
  async queryBaseInfosByMobile ({ commit }, mobile) {
    const result = await AuthApi.queryBaseInfosByMobile(mobile)
    return result
  },
  async getMerchantBaseInfo ({ commit }, params) {
    const result = await AuthApi.getMerchantBaseInfo(params)
    if (result && result.homePage && result.homePage.enable === 1) {
      commit('SET_BASE_INFO', result)
    }
    return result
  },
  async registerMerchant ({ commit }, data) {
    const result = await AuthApi.registerMerchant(data)
    return result
  },
  async getAccountByPhoneCode({ commit }, data) {
    const result = await AuthApi.getAccountByPhoneCodeApi(data)
    return result
  },
  async getWxLoginParams ({ commit }) {
    const result = await AuthApi.getWxLoginParams()
    return result
  },
  async wxLogin ({ commit }, data) {
    const result = await AuthApi.wxLogin()
    return result
  },
  async getWxLoginUuid ({ commit }) {
    const result = await AuthApi.getWxLoginUuid()
    return result
  },
  async getWxLoginAccountsByCode ({ commit }, code) {
    const result = await AuthApi.getWxLoginAccountsByCode(code)
    return result
  },
  async accountLoginByUuid ({ commit }, data) {
    // 切换账号时，先清空旧的商户信息
    await storeGlo.commit('MerchantMe/SET_MERCHANT_INFO', null)
    
    const result = await AuthApi.accountLoginByUuid(data)
    if (result.token) {
      commit('SET_ACCESS_TOKEN', result.token)
      setAccessToken(result.token)
    }
    if (result.username) {
      commit('SET_USERNAME', result.username)
      setUsername(result.username)
    }
    if (result.id) {
      commit('SET_ADMIN_ID', result.id)
      setAdminId(result.id)
    }
    // 获取商户信息详情 用于获取手机号
    const rets = await MerchantAdminApi.getMerchantDet(result.id)
    commit('SET_MOBILE', rets.mobile)
    setMobile(rets.mobile)
    
    // 如果登录时传入了 merchantId，先设置一个临时的 merchantInfo 对象
    // 这样请求拦截器就能在调用 getMerchantInfo 时自动带上 merchantId 参数
    if (data.merchantId) {
      storeGlo.commit('MerchantMe/SET_MERCHANT_INFO', {
        merchantId: data.merchantId
      })
    }
    
    return result
  },
  async getOpenAccounts ({ commit }) {
    const result = await AuthApi.getOpenAccounts()
    return result
  },
  // 重置密码
  async resetPassword ({ commit }, params) {
    const result = await AuthApi.resetPassword(params)
    return result
  },
  // 获取商户列表（LOGIN_TEMP场景）
  async getMerchants ({ commit }, tempToken) {
    const result = await AuthApi.getMerchants(tempToken)
    return result
  },
  // 选择商户并获取正式token（LOGIN_TEMP场景）
  async selectMerchant ({ commit }, params) {
    const result = await AuthApi.selectMerchant(params)
    
    // 如果成功返回正式token，设置到store中
    if (result) {
      // 根据API文档，token字段可能是 result.t 或 result.token
      const accessToken = result.t || result.token
      if (accessToken) {
        commit('SET_ACCESS_TOKEN', accessToken)
        setAccessToken(accessToken)
      }
      if (result.username) {
        commit('SET_USERNAME', result.username)
        setUsername(result.username)
      }
      if (result.id) {
        commit('SET_ADMIN_ID', result.id)
        setAdminId(result.id)
      }
    }
    
    return result
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/merchant-auth.js